import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { X } from "lucide-react";

const ZoomContext = createContext(null);

/**
 * ZoomProvider — mount once near the app root. Exposes `openZoom(html)` via
 * `useZoom()`. Any component (Markdown, MessageBubble, etc.) can call it to
 * pop a centered modal that displays cloned HTML (SVG diagram, table, image)
 * at max-readable size.
 *
 * - Click outside content, ESC, or × button closes the modal.
 * - Body scroll is locked while open; restored on close.
 * - The modal mounts conditionally (display:none until open).
 */
export function ZoomProvider({ children }) {
  const [html, setHtml] = useState(null);
  const lastTrigger = useRef(null);
  const lastOpenAt = useRef(0);

  const openZoom = useCallback((rawHtml, triggerEl) => {
    // debounce rapid double-clicks to prevent stacking
    const now = Date.now();
    if (now - lastOpenAt.current < 250) return;
    lastOpenAt.current = now;
    lastTrigger.current = triggerEl || null;
    setHtml(rawHtml);
  }, []);

  const closeZoom = useCallback(() => {
    setHtml(null);
    // restore focus to the original clicked element
    const t = lastTrigger.current;
    if (t && typeof t.focus === "function") {
      try {
        t.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    }
  }, []);

  // ESC to close + lock body scroll while open
  useEffect(() => {
    if (!html) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [html, closeZoom]);

  return (
    <ZoomContext.Provider value={{ openZoom, closeZoom }}>
      {children}
      {html ? <ZoomModal html={html} onClose={closeZoom} /> : null}
    </ZoomContext.Provider>
  );
}

export function useZoom() {
  // Safe fallback so components that render outside a ZoomProvider (e.g.
  // standalone storybook) still work — zoom just becomes a no-op.
  return useContext(ZoomContext) || { openZoom: () => {}, closeZoom: () => {} };
}

function ZoomModal({ html, onClose }) {
  const contentRef = useRef(null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Zoomed view"
      data-testid="zoom-modal"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 animate-zoom-fade"
      style={{ background: "rgba(0,0,0,0.85)" }}
    >
      <div
        ref={contentRef}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-[90vw] max-h-[90vh] w-auto h-auto overflow-auto rounded-xl bg-[#0F0F0E] border border-[#b89846]/40 p-6"
        style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)" }}
      >
        <button
          type="button"
          aria-label="Close zoomed view"
          onClick={onClose}
          data-testid="zoom-close-btn"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-[#171615] border border-[#b89846]/40 text-[#e8c879] hover:text-white hover:bg-[#b89846]/20 transition flex items-center justify-center"
        >
          <X size={16} strokeWidth={2} />
        </button>
        <div
          className="zoom-content text-white"
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}
